import { useState } from 'react'
import { Link } from 'react-router-dom'
import { tournamentApi } from '../services/api'
import { useLanguage } from '../context/LanguageContext'
import LanguageToggle from '../components/LanguageToggle'

/**
 * Reseller "forgot password" request page.
 * Posts the email to the shared cms_users reset flow
 * (POST /api/auth/forgot-password). The backend resolves the reset link
 * back to this site's origin, so the emailed link lands on /reset-password.
 */
export default function ForgotPassword() {
  const { t } = useLanguage()
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState('')

  async function handleSubmit(e) {
    e.preventDefault()
    setError(''); setSubmitting(true)
    try {
      await tournamentApi.post('/auth/forgot-password', { email: email.trim() })
      setDone(true) // backend returns a generic message either way (no email enumeration)
    } catch (err) {
      setError(err?.response?.data?.message || err.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Wrapper>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={eyebrow}>{t('sp_play_and_stay_portal_caps', 'PLAY AND STAY PORTAL')}</div>
        <h1 style={{ margin: 0, fontSize: 24, color: 'var(--secondary-950)' }}>{t('sp_reset_your_password', 'Reset your password')}</h1>
        <p style={{ marginTop: 12, color: '#64748b', fontSize: 14 }}>
          {t('sp_forgot_password_intro', "Enter your account email and we'll send you a link to reset your password.")}
        </p>
      </div>

      {done ? (
        <>
          <div style={successBox}>
            {t('sp_reset_link_sent', 'If an account with that email exists, a reset link has been sent. Check your inbox (and spam) — the link expires in 15 minutes.')}
          </div>
          <Link to="/login" style={backLink}>{t('sp_back_to_sign_in', '← Back to sign in')}</Link>
        </>
      ) : (
        <form onSubmit={handleSubmit}>
          {error && <div style={errorBox}>{error}</div>}
          <label style={labelStyle}>{t('email', 'Email')}</label>
          <input
            type="email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus
            style={inputStyle} placeholder={t('sp_email_placeholder', 'you@yourgolfcourse.com')}
          />
          <button type="submit" disabled={submitting} className="btn btn-dark" style={{ width: '100%', marginTop: 20, padding: '14px', opacity: submitting ? 0.6 : 1 }}>
            {submitting ? t('sp_sending_ellipsis', 'Sending…') : t('sp_send_reset_link', 'Send reset link')}
          </button>
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <Link to="/login" style={backLink}>{t('sp_back_to_sign_in', '← Back to sign in')}</Link>
          </div>
        </form>
      )}
    </Wrapper>
  )
}

function Wrapper({ children }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, var(--brand-50) 0%, #fff 50%, var(--brand-100) 100%)', padding: 20 }}>
      <div className="card" style={{ padding: 40, width: '100%', maxWidth: 440 }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
          <LanguageToggle />
        </div>
        <Link to="/" style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
          <img src="/integolf-logo.svg" alt="InteGolf" style={{ height: 40 }} />
        </Link>
        {children}
      </div>
    </div>
  )
}

const eyebrow = { fontSize: 11, letterSpacing: 2, color: 'var(--brand-600)', fontWeight: 700, marginBottom: 6 }
const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--secondary-800)', marginBottom: 6 }
const inputStyle = { width: '100%', padding: '12px 14px', border: '1px solid var(--input-border)', borderRadius: 8, fontSize: 14, outline: 'none', fontFamily: 'inherit', color: 'var(--text)', boxSizing: 'border-box' }
const successBox = { padding: 14, background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0', borderRadius: 8, fontSize: 14, lineHeight: 1.5, marginBottom: 16 }
const errorBox = { padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 8, marginBottom: 14, fontSize: 13 }
const backLink = { fontSize: 14, color: 'var(--brand-600)', fontWeight: 600, textDecoration: 'none' }
